// User (member) manual. Covers every regular member on UserLayout —
// including the Manager / Team Leader / Employee Work-Role tiers, which are
// NOT separate panels but the same panel gated by Work-Role operations + rank.
//
// Block shapes (shared across all manuals — see ./index.js):
//   'text'                              → paragraph
//   { sub: 'text' }                     → subheading
//   { list: ['a', 'b'] }                → bulleted list
//   { steps: ['a', 'b'] }               → numbered list
//   { table: { head: [...], rows: [...] } }
//   { note: 'text' }                    → highlighted callout
//   { verify: 'text' }                  → "Needs verification" callout
//   { image: '/manual/x.png', caption?, alt? } → screenshot (public/manual/)

const manual = {
  key: 'user',
  role: 'User',
  title: 'User Manual — Member',
  subtitle:
    'For everyone who works in TaskMagix day to day: employees, team leaders and managers.',
  sections: [
    {
      id: 'welcome',
      title: 'Welcome',
      blocks: [
        'TaskMagix is your team’s workspace. It holds your tasks, the Spaces and Lists they live in, the Standard Operating Procedures (SOPs) you follow, and the Work Reports you file. Everything you own is reachable from the left sidebar.',
        'What you can see and do depends on your Work Role. A Work Role is a set of permissions your Company Admin defines — there are no fixed "Manager", "Team Leader" or "Employee" accounts. Your role may give you wider visibility (your department, or the whole company) and extra pages (Team Overview, Team Tasks, Team Reports). If a menu described here is missing from your sidebar, your Work Role does not have that permission.',
        { sub: 'The Manager / Team Leader / Employee tiers' },
        'Work Roles sit in a seniority order (rank). A more senior person can see peers at their own rank and everyone more junior, and can delegate tasks down the order — but never up to someone more senior. Managers usually have company-wide visibility, Team Leaders department-wide, and Employees see their own work. All three use the same panel.',
        {
          image: '/manual/welcome.png',
          alt: 'TaskMagix workspace overview',
        },
      ],
    },
    {
      id: 'page-guide',
      title: 'Page-by-Page Guide',
      blocks: [
        'This guide follows your sidebar top to bottom. Which items appear depends on your Work Role and your company’s plan. The last few sit outside the sidebar: the top bar, the bell and your avatar menu.',

        { sub: 'Dashboard' },
        'Your home page, at the top of the sidebar. The Daily Report card carries Submit Daily Report and View My Reports — plus Submit for [date] when you missed a past day. When a Weekly or Monthly report is due, a Submit [cadence] Report button appears. If your Work Role is supervisory, the Action Needed panel adds quick actions: Assign Task, Review Team Tasks and View Team.',
        {
          image: '/manual/dashboard.png',
          caption: 'The Dashboard — your Daily Report card and (for supervisory roles) the Action Needed panel.',
        },

        { sub: 'SOPs Library' },
        'Browse published procedures. Search, filter by Department and sort by Updated/Title. Open an SOP card to read it and Download as PDF. If your plan and role allow, you can turn an SOP into tasks — you review the breakdown before the tasks are created.',
        {
          image: '/manual/sops-library.png',
          caption: 'SOPs Library — search and filter procedures, then open a card to read or turn it into tasks.',
        },

        { sub: 'Files & Links' },
        'The shared library of files and links. Search, and use per-file actions (download, rename, delete) that depend on your access. A storage card shows how much of the plan’s space is used. External collaborators see only files in the Spaces they were granted.',
        {
          image: '/manual/files-links.png',
          caption: 'Files & Links — the shared library with per-file actions and a storage summary.',
        },

        { sub: 'Leaderboard' },
        'The company points ranking, shown when gamification is on. A period selector (This Week / Month / Year) — plus a Department filter for company-wide roles — drives a "My Stats" card and a ranked table.',
        {
          image: '/manual/leaderboard.png',
          caption: 'Leaderboard — the points ranking with a period selector and your My Stats card.',
        },

        { sub: 'Spaces — a Space and the task panel' },
        'The Spaces tree — or a Space Explorer for company-wide roles. A Space opens on one of three tabs: Overview, Lists and Board. Add Task creates a task. On the Board, drag a card between columns to change its status. Click a task to open the task panel:',
        {
          table: {
            head: ['Control', 'What it does'],
            rows: [
              ['Task name', 'Click to rename, if you have edit rights.'],
              ['Status', 'Shown as a pill. Change it from the list row, or by dragging on the Board.'],
              ['Priority', 'Editable dropdown (Urgent / High / Normal / Low). Locked once the task is completed, and for viewers without planning rights.'],
              ['Assignees', 'Avatar stack with an "Add people" picker. You can only pick people who can open this Space and are not more senior than you.'],
              ['Start / Due Date and Time', 'A date picker with presets (Today, Tomorrow, Next week…) and an "Add time" toggle. With no time, a due date means end of day (11:59 PM).'],
              ['Description', 'A free-text box ("Enter Description", up to 5000 characters).'],
              ['Files & Links', 'Attach files or links to the task.'],
              ['Comments', 'Post comments and @mention colleagues. Replies notify them.'],
              ['Activity', 'A log of status, priority, assignee and other changes.'],
            ],
          },
        },
        'Subtasks nest up to two levels (Task → Subtask → Sub-subtask). You cannot complete a parent task while any subtask is unresolved.',
        {
          image: '/manual/spaces.png',
          caption: 'A Space in List view with the task panel open — status, priority, assignees, dates and comments.',
        },

        { sub: 'Supervision › Team Overview / Team Tasks / Team Reports (if granted)' },
        'The Supervision menu appears when your Work Role has department-wide or company-wide access. Team Overview is a member directory with per-person stats (open, overdue, reports, points), plus search and a role filter. Team Tasks lists your team’s tasks in List and Gantt views, with Status/Priority/Assignee/Due filters. Team Reports lists your team’s submitted reports, with a member search and an Overdue-only filter. Open a row to read a report.',
        {
          image: '/manual/team-overview.png',
          caption: 'Supervision › Team Overview — your team’s members with per-person stats.',
        },

        { sub: 'Supervision › Report Settings (if granted)' },
        'Appears only if your Work Role can view team reports. Pick a Department, then edit the two lists used in that department’s reports: Task Types and Item Status. Each list has an Add Option button and a per-row delete. Nothing is saved until you click Save Changes.',
        {
          note: 'Important: a department’s Task Types and Item Statuses start EMPTY. The report form’s Task Type and Item Status options come from these lists, so until you set them here, that department’s members can’t fill in their Daily / Weekly / Monthly Work Reports properly.',
        },
        {
          image: '/manual/report-settings.png',
          caption: 'Supervision › Report Settings — set a department’s Task Types and Item Statuses so its team can file reports.',
        },

        { sub: 'Reports — Daily / Weekly / Monthly' },
        'Each report page has a date/period picker at the top, then the report form (see the Reports section for the full field list). Weekly and Monthly pages add a Draft with AI button that rolls up your dailies into a starting draft. The action buttons are Clear All, Save Draft and Submit. Once submitted, a report is locked until reopened. The daily report caps logged time at 12 hours.',
        {
          image: '/manual/reports.png',
          caption: 'A report page — the date/period picker, the report form and the Save Draft / Submit actions.',
        },

        { sub: 'Elsewhere in the app (not the sidebar)' },
        'Three surfaces sit outside the sidebar:',
        {
          list: [
            'Focus Board — open it from the button in the top bar. A "what should I do now" board that groups tasks into Overdue, Due Today, Due Tomorrow and Upcoming, with a points-and-badges side card.',
            'Notifications — the header bell → View all notifications. Filter by type, Mark all as read, and per row mark-as-read / archive / delete. Space/List invitations show inline Accept / Decline.',
            'Profile — from your avatar menu. Upload an avatar (JPEG/PNG/GIF/WebP, ≤2 MB), edit your Full Name and Contact Number, and change your password (current, new — 8–64 chars with upper, lower, number and symbol — and confirm).',
          ],
        },
      ],
    },
    {
      id: 'daily-workflow',
      title: 'Daily Workflow',
      blocks: [
        'A typical day as a member:',
        {
          steps: [
            'Log in — you land on your Dashboard.',
            'Check the Dashboard: note the Due Today and Overdue counts, and scan Today’s Focus.',
            'Open your tasks from a Space, List or the dashboard, and start work.',
            'Update task status and log time as you go. Add comments or @mention colleagues when you need input.',
            'Follow any SOP steps assigned to you and mark them done.',
            'Before end of day, submit your Daily Report — the card turns amber when it is due.',
            'Check the header bell for mentions, assignments and replies.',
          ],
        },
      ],
    },
    {
      id: 'task-management',
      title: 'Task Management',
      blocks: [
        { sub: 'Spaces, Projects and Lists' },
        'A Space is a container for a team or area of work. Inside a Space, a Project (shown as "Project" in the UI) groups related Lists, and a List holds tasks. Every Project starts with a "Tasks List" by default.',
        { sub: 'Tasks and Subtasks' },
        'A task has a name, status, priority, assignees, start/due dates (optionally with a time of day), a description, comments and attachments. Tasks can have subtasks nested up to two levels deep (Task → Subtask → Sub-subtask). Card counts include subtasks by their own status.',
        {
          note: 'What you may edit on a task depends on your access level in that Space (Full, Edit, Comment or View) and your Work Role. People who handle the task can change its status; some planning fields are limited to department handlers. You cannot complete a parent task while any subtask is still unresolved.',
        },
        { sub: 'SOPs' },
        'An SOP is a written procedure. If your plan and role allow, you can turn an SOP into a set of tasks in one step. You review the breakdown in the "Review AI-Generated Tasks" dialog before the tasks are created, and can save it as a reusable blueprint so re-running it costs nothing.',
        { sub: 'Delegation' },
        'If your Work Role grants the delegate permission, you can assign tasks to other members. The rank rule applies: you cannot assign a task to someone more senior than you. You can always assign to yourself, and to more-junior members within your visible scope.',
        { sub: 'Assignees' },
        'You can only assign a task to people who can open that Space: its department members, plus anyone with company-wide reach. If someone is not in the picker, they cannot see that Space.',
        { verify: 'Admins configure Approvals and Workflows; you follow whatever status flow the company has set up. Your Company Admin defines the exact set of statuses per department.' },
      ],
    },
    {
      id: 'verification',
      title: 'Task Verification & Proof',
      blocks: [
        'Some tasks must be checked before they count as done. In a task’s detail panel, under "Accountability", a task may show:',
        {
          list: [
            'Checker — the person who will verify the task.',
            'Expected output — what the task should produce. Read this before you start.',
            'Proof required — when on, you must attach a file or link as proof before you can complete the task.',
          ],
        },
        { sub: 'Submitting your work' },
        {
          steps: [
            'Do the work. If proof is required, attach it to the task — a photo, screenshot, report or document.',
            'Click Submit for verification. The task moves to "Awaiting checker" and the Checker is notified.',
            'If it comes back as "Changes requested", read the Checker’s note, fix the issue, and submit again.',
          ],
        },
        {
          note: 'You can’t mark a task with a Checker complete yourself — it completes when the Checker approves it. You’ll get reminders, and if it sits too long it’s escalated to your supervisor.',
        },
        { sub: 'If you are the Checker' },
        'When a task lists you as its Checker, open it once it’s "Awaiting checker" and use Approve (the task completes) or Reject. A rejection needs a short note explaining what to fix. That note is shown to the task’s owner.',
        { sub: 'AI proof check' },
        'If your company has turned it on, an "AI proof check" panel may appear on the task. It gives an advisory verdict (e.g. looks correct / needs review) with a short summary of what the AI saw in your proof. It is guidance only — the human Checker still makes the final call. A flag doesn’t mean your work is wrong, just that it’s worth a closer look.',
      ],
    },
    {
      id: 'reports',
      title: 'Reports',
      blocks: [
        'You file three kinds of Work Report. Your Company Admin decides which are required and when each is due.',
        {
          table: {
            head: ['Report', 'Covers', 'When'],
            rows: [
              ['Daily Report', 'A single day', 'By the daily reminder time, if daily reporting is enabled'],
              ['Weekly Report', 'One working week (the company’s active working days)', 'By the set day of the week'],
              ['Monthly Report', 'A calendar month (previous or current, per company setting)', 'By the set day of the month'],
            ],
          },
        },
        { sub: 'What a report contains' },
        {
          list: [
            'Summary — a short free-text overview.',
            'Work Items — rows with title, description, Project, Module, Type, Status, Priority and Time Spent. Linking a task auto-fills several fields. The daily report caps time at 12 hours per day.',
            'Key Achievements / Deliverables.',
            'Issues & Dependencies — blockers and what you are waiting on, each with a resolution status.',
            'Plans for the Next Day.',
            'Meetings Attended — with purpose, duration and attendees.',
            'Notes.',
          ],
        },
        { sub: 'AI draft' },
        'On Weekly and Monthly reports, use "Draft with AI" to roll up that period’s daily reports into a starting draft, which you then edit before submitting.',
        {
          note: 'Task Type and Item Status options come from your company’s Work Report Settings, set per department, so the choices you see match your area. Reported hours include time logged on tasks plus meeting durations.',
        },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications & FAQs',
      blocks: [
        'Notifications reach you through the bell in the header. Open "View all notifications" to see the full list. There is no separate sidebar row for notifications.',
        'As a member you can receive, among others:',
        {
          list: [
            'Task assigned / unassigned to you, and task status changed or completed.',
            'Task due soon and task overdue reminders. Your company sets how far ahead these fire.',
            'Comment added on a task you are involved in, and mentions when you are @mentioned or replied to.',
            'Badge awarded when you earn recognition.',
            'Space and List invitations, and access-level changes.',
            'Announcements from your Company Admin or the platform.',
          ],
        },
        'On the notifications page you can filter by type, search, mark items read, and delete them. Clicking a notification marks it read and opens the related item.',
        { sub: 'Frequently Asked Questions' },
        { sub: 'A menu I was told about is missing. Why?' },
        'Sidebar items depend on your Work Role permissions and your company’s plan. If Team Overview, Team Tasks or Team Reports are missing, your Work Role hasn’t been granted them. Ask your Company Admin.',
        { sub: 'Why can’t I assign a task to a particular colleague?' },
        'Either they cannot see that Space — only department members and company-wide roles appear in the picker — or they are more senior than you, and you cannot delegate up the seniority order.',
        { sub: 'Why can’t I complete a task?' },
        'You cannot complete a task until every subtask is resolved. Also, completed tasks lock their priority until reopened.',
        { sub: 'My report won’t submit.' },
        'A report needs at least one meaningful entry, and task names must contain at least one letter. You cannot submit a Weekly or Monthly report empty.',
        { sub: 'Do I have to file a report every day?' },
        'Only if your Company Admin has enabled daily reporting and today is an active working day. The Daily Report card tells you when one is due.',
      ],
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      blocks: [
        {
          list: [
            'Start each day on the Dashboard — clear Overdue first, then Due Today.',
            'Keep task status current so your team’s dashboards and reports stay accurate.',
            'Log time as you go, rather than reconstructing it at report time.',
            'Star the Spaces and Lists you use most so they sit in Favourites.',
            'Use @mentions instead of side-channels so context stays on the task.',
            'File your Daily Report before you leave — the Weekly/Monthly AI draft only works well when the dailies exist.',
            'Follow the SOP for a process rather than improvising. It keeps everyone consistent.',
          ],
        },
      ],
    },
  ],
}

export default manual
